<script lang="ts">
  // Die Startseite: vier Zonen (System, Energie & Klima, Zuhause, Live),
  // jede mit Kopf (Symbol in der Farbe ihres Bereichs, Name, Lage-Satz)
  // und Karten darunter -- Kennzahlen dürfen Karten sein.
  //
  // Seit 03.10.2026 in der gemeinsamen Sprache (docs/gestaltung.md):
  //   - Zonen-Kopf mit Lage-Satz statt grauem Etikett
  //   - System als Kennzahl-Karten wie auf der Status-Seite; der Balken
  //     mit der dringendsten Meldung steht nur da, wenn es eine gibt
  //     (kein grüner Dauer-Puls: ein Dauerzustand blinkt nicht)
  //   - jeder Zustand über StatusBadge, Kategorien als Schild
  //   - Kurven in der Farbe ihrer Messgröße (SparkLine platz)
  //   - deutsche Zahlen („72,2 W"), Aktionen ohne Rahmen
  // Rechnung der Lage-Sätze: lib/dashboard/zonen.ts.
  import Icon, { type IconName } from "../lib/Icon.svelte";
  import Lage from "../lib/Lage.svelte";
  import Schild from "../lib/Schild.svelte";
  import SparkLine from "../lib/SparkLine.svelte";
  import Chart from "../lib/Chart.svelte";
  import { LUEFTUNG_FUEHLER, aufEineAchse, heizPhasen, lueftungLage, type Messpunkt } from "../lib/dashboard/lueftung";
  import StatusBadge from "../lib/StatusBadge.svelte";
  import SymbolKnopf from "../lib/SymbolKnopf.svelte";
  import ZonenKopf from "../lib/dashboard/ZonenKopf.svelte";
  import { istDiagnose, klimaLage, messgroesse, messwert, offenZuLang, tuerTon, zuhauseLage, type Tuer } from "../lib/dashboard/zonen";
  import { artTon } from "../lib/ereignisse/ereignisse";
  import { REIHE } from "../lib/farbe/farbe";
  import type { LageTeil } from "../lib/lage";
  import { stackVersions } from "../lib/build-info";
  import { activeDashboardSections } from "../lib/dashboards.svelte";
  import { APIError, postJSON } from "../lib/api";
  import { toast } from "../lib/toast.svelte";
  import { formatRelative, formatUptime, formatZahl } from "../lib/format";
  import { getClusterStats } from "../lib/cluster-stats.svelte";
  import { getHostsSummary } from "../lib/hosts-summary.svelte";
  import CardGrid from "../lib/CardGrid.svelte";
  import { resetLayout } from "../lib/card-layout";
  import CardConfigDialog from "../lib/CardConfigDialog.svelte";
  import {
    getCardConfig,
    resetCardConfig,
    setCardConfig,
    type CardConfig,
    type ZahlAbschnitt,
  } from "../lib/card-config";
  import { getJSON } from "../lib/api";
  import { abrufAb, fortschreiben, type Fenster, type VerlaufAntwort } from "../lib/verlauf-fenster";
  import { resetCardOrder } from "../lib/card-order";
  import { connectivityState, serverClockMs } from "../lib/heartbeat.svelte";
  import { t } from "../lib/i18n.svelte";
  import { getAktorenSummary, refreshAktorenSummary } from "../lib/aktoren-summary.svelte";
  import {
    getAmbient,
    getScenes,
    refreshAmbient,
    type AmbientView,
    type SceneView,
  } from "../lib/ambient-summary.svelte";
  import { getUptimeSummary, type UptimeTarget } from "../lib/uptime-summary.svelte";
  import { getEnergySummary } from "../lib/energy-summary.svelte";
  import { isEntityDisabled } from "../lib/entity-disables.svelte";
  import { devices as iotDevices, ensureDevices } from "../lib/iot-devices.svelte";
  import { getKlimaSummary } from "../lib/klima-summary.svelte";
  import { clearPageNav, setPageNav } from "../lib/page-nav.svelte";
  import { setPageHeader } from "../lib/page.svelte";
  import RemediationMenu from "../lib/RemediationMenu.svelte";
  import { remediationsForCritical } from "../lib/remediations.svelte";
  import { getSummary } from "../lib/security.svelte";
  import { getTasksSummary } from "../lib/tasks-summary.svelte";
  import { getCriticalSummary } from "../lib/critical-summary.svelte";
  import { getAgents } from "../lib/agents-summary.svelte";
  import { getStorageSummary } from "../lib/storage-summary.svelte";
  import { getWan } from "../lib/wan-summary.svelte";
  import { internetAllesOk, probeRouter, routerKurz, routerName, weitereRouter } from "../lib/netzwerk/netz";
  import { getAlertsVisible } from "../lib/alerts-store.svelte";
  import { SEVERITY_TEXT } from "../lib/meldungs-logik";
  import { getNetzfrequenz, hzText, mhzText } from "../lib/netzfrequenz-summary.svelte";
  import { getWeatherSummary, weatherIconName, weatherIconFromCode } from "../lib/weather-summary.svelte";
  import { router } from "../lib/router.svelte";
  import type { AppEvent } from "../lib/types";
  import { getEvents, refreshEvents } from "../lib/events-summary.svelte";
  import { subscribe as wsSubscribe } from "../lib/ws-hub.svelte";
  import { abonniereFeinstaub, feinstaubEntitaeten } from "../lib/protokoll/feinstaub.svelte";
  import { seiteSichtbar } from "../lib/shell/page-route";

  // Summary-Stores werden in App.svelte beim Boot gestartet
  // (startBootStreams). Dashboard liest nur via $derived(getXxx()).

  // Praesenz-Kanal: solange die Internet-Karte sichtbar ist, bittet
  // das Backend den OpenWrt-Agent um 2-s-Takt (Boost-on-View). Kein
  // Payload -- das Abonnement selbst ist das Signal. Nur solange man die
  // Seite sieht: als gehaltener Tab oder im Hintergrund-Fenster hielt das
  // Abo den Agenten sonst dauerhaft im 2-s-Takt.
  const sichtbar = seiteSichtbar();
  $effect(() => {
    if (!sichtbar()) return;
    return wsSubscribe("openwrt:live", () => {});
  });
  // Pilot Homelab-Protokoll (docs/protokoll.md, „Browser (Phase 4)"): die
  // Feinstaubwerte der Geräte-Karte kommen aus feinstaub.*.luft, ebenfalls
  // nur solange die Seite zu sehen ist.
  $effect(() => {
    if (!sichtbar()) return;
    return abonniereFeinstaub();
  });

  $effect(() => {
    // Dashboard hat seinen eigenen Hero im Content; ein Page-Header
    // in der globalen Toolbar/PageToolbar waere doppelt. Leerer Wert
    // unterdrueckt das Rendern der PageToolbar (Rail-Modus) bzw.
    // der Title-Mitte (Pill-Modus).
    setPageHeader("", "");
  });

  // Versionsdaten fuer den Toolbar-Info-Popover. Beim Mount einmal
  // ziehen -- nach Service-Restart aktualisiert sich das durchs
  // re-mount automatisch.
  interface VersionInfo {
    version: string;
    commit: string;
    build_time: string;
    go_version: string;
  }
  let versionInfo = $state<VersionInfo | null>(null);
  $effect(() => {
    void fetch("/v2-api/version", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((v: VersionInfo | null) => (versionInfo = v))
      .catch(() => undefined);
  });

  // setPageNav registriert die Toolbar-Slots fuer das Dashboard --
  // kein Back-Button (Root), nur Info-Popover mit Version + Commit.
  $effect(() => {
    setPageNav({ info: dashboardInfo, infoTitle: "Über Homelab" });
    return () => clearPageNav();
  });

  // Aktives Dashboard-Preset (geraetelokal): steuert welche Sektionen
  // sichtbar sind. Ausblenden via class:hidden statt {#if} -- eine Zeile
  // pro Sektion, kein Wrappen der Riesenbloecke. Der Preset-Umschalter
  // lebt in der Suche (NavSearchPanel, Dashboard-Zeile), damit er
  // keine Seitenhoehe kostet.
  const dashSections = $derived(activeDashboardSections());

  // Ereignisse kommen aus dem Store (lib/events-summary): WS-Push ueber
  // den gemeinsamen Hub, SWR-Snapshot fuer das erste Bild, Poll nur als
  // Netz darunter. Die Seite liest hier nur noch.
  const events = $derived(getEvents());
  let now = $state(new Date());
  // Grobe Uhr (10 s) fuer die Frische der Feinstaub-Protokollwerte (Verfall
  // nach 120 s): nur Feinstaub-Karten lesen sie, alle anderen Geraete-Karten
  // haengen nicht an einer tickenden Uhr.
  let frischeMs = $state(serverClockMs() ?? Date.now());

  $effect(() => {
    // Frisch ziehen, wenn jemand die Uebersicht tatsaechlich oeffnet.
    void refreshEvents();
    const tickId = setInterval(() => (now = new Date()), 1_000);
    const frischeId = setInterval(() => (frischeMs = serverClockMs() ?? Date.now()), 10_000);
    return () => {
      clearInterval(tickId);
      clearInterval(frischeId);
    };
  });

  // Pre-compute relative-time strings driven by `now`. Im each-Block
  // ohne $derived-Wrap ist Svelte 5 zickig (Tabelle hängte zuvor auf
  // "gerade eben").
  const eventsRendered = $derived(
    events.map((e) => ({ ...e, rel: formatRelative(e.ts, now) })),
  );

  // Feed-Clustering (Redesign 2026-07-18): aufeinanderfolgende Task-ok-
  // Events innerhalb eines Zeitfensters falten zu EINER aufklappbaren
  // Zeile ("N Tasks ok") -- der Watchdog-Puls alle paar Minuten soll
  // den Feed nicht fluten. Reine Praesentation, die Rohdaten bleiben
  // unveraendert (alle Ereignisse: /system/events).
  type RenderedEvent = AppEvent & { rel: string };
  type ClusterItem = RenderedEvent & { taskName: string; taskDur: string };
  type FeedRow =
    | { kind: "single"; ev: RenderedEvent }
    | { kind: "cluster"; key: string; rel: string; items: ClusterItem[] };
  const TASK_OK_RE = /^(.*?) ok in ([\d.,]+\s*(?:ms|s|m|h))$/;
  const CLUSTER_WINDOW_MS = 15 * 60_000;

  const feedRows = $derived.by<FeedRow[]>(() => {
    const rows: FeedRow[] = [];
    let clusterStartTs = 0;
    for (const ev of eventsRendered) {
      const m = ev.domain === "tasks" && ev.severity === "info" ? TASK_OK_RE.exec(ev.summary) : null;
      if (m) {
        const ts = Date.parse(ev.ts);
        const last = rows[rows.length - 1];
        if (last?.kind === "cluster" && clusterStartTs - ts <= CLUSTER_WINDOW_MS) {
          last.items.push({ ...ev, taskName: m[1], taskDur: m[2] });
          continue;
        }
        clusterStartTs = ts;
        rows.push({ kind: "cluster", key: "c" + (ev.id ?? ev.ts), rel: ev.rel, items: [{ ...ev, taskName: m[1], taskDur: m[2] }] });
        continue;
      }
      rows.push({ kind: "single", ev });
    }
    // Einer-"Cluster" als normale Zeile zeigen, dann Anzeige kappen.
    return rows
      .map((r): FeedRow => (r.kind === "cluster" && r.items.length === 1 ? { kind: "single", ev: r.items[0] } : r))
      .slice(0, 10);
  });

  // Aufgeklappte Cluster (Set statt Mutation auf den Row-Objekten,
  // siehe button_state_management).
  let openClusters = $state<Set<string>>(new Set());
  function toggleCluster(key: string): void {
    const next = new Set(openClusters);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    openClusters = next;
  }

  // Ein Ereignis trägt Farbe nur als Befund (Warnung, Fehler); sein
  // Bereich ist eine Kategorie und steht neutral als Schild davor.
  function zeilenTon(sev: string): string {
    const ton = artTon(sev);
    return ton === "danger" ? "text-danger font-medium" : ton === "warn" ? "text-warning font-medium" : "";
  }
  function eventClickTarget(ev: AppEvent): string | null {
    if (ev.domain === "fail2ban") return "/security";
    if (ev.domain === "tasks") return "/system/tasks";
    return null;
  }
  function openEvent(ev: AppEvent) {
    const target = eventClickTarget(ev);
    if (target) window.location.hash = target;
  }
  // Sicherheit: aktive Bans (Chip "Bans" im System-Streifen).
  const security = $derived(getSummary());

  // Uptime-Card: Erreichbarkeit der ueberwachten URLs (je Blickwinkel).
  // status=problem sobald mind. eine Zeile entprellt-down ist. down[]
  // haelt die Down-Zeilen fuer die Detail-Zeile.
  const uptime = $derived(getUptimeSummary());
  const uptimeCard = $derived.by(() => {
    if (!uptime || uptime.total === 0) {
      return { status: "loading" as const, up: 0, total: 0, down: [] as UptimeTarget[] };
    }
    const down = uptime.targets.filter((tt) => tt.health === "down");
    return {
      status: (down.length > 0 ? "problem" : "ok") as "problem" | "ok",
      up: uptime.up,
      total: uptime.total,
      down,
    };
  });

  // Tasks: Laeufe in 24 h + gerade rote Tasks (Chip im System-Streifen).
  const tasks = $derived(getTasksSummary());

  // Kritische Ereignisse: nur noch fuer den Massnahmen-Knopf am Kopf des
  // System-Streifens (remediation_cluster). Angezeigt wird Kritisches ueber
  // die Meldungen (alerts-store), nicht mehr als eigenes Banner.
  const critical = $derived(getCriticalSummary());
  const criticalEvents = $derived(critical?.events ?? []);

  // System-Karte: Uhrzeit (tickt via `now`) + Homelab-Uptime (forge, der
  // Host auf dem alles laeuft) + Kritisch-Zaehler unten.
  // Uhr aus dem Server-Heartbeat (ping-Channel): zeigt die SERVER-Zeit,
  // friert ein + wird stale, wenn der Server nicht mehr atmet. Bis zum
  // ersten Ping Fallback auf die Browser-Zeit (now), damit nichts blinkt.
  const connState = $derived(connectivityState());
  const hbStale = $derived(connState === "no_net" || connState === "server_unreachable");
  const clockText = $derived.by(() => {
    const ms = serverClockMs();
    const d = ms != null ? new Date(ms) : now;
    return d.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  });
  const forgeUptimeS = $derived.by((): number | null => {
    const cs = getClusterStats();
    if (!cs) return null;
    for (const c of cs) {
      for (const h of c.hosts) {
        if (h.id === "forge") return h.stats?.uptime_s ?? null;
      }
    }
    return null;
  });

  // ─── Karten-Anordnung pro Zone ──────────────────────────────────────
  // Reihenfolge persistiert in user_preferences (prefs-Store, Server-SSOT
  // + WS-Sync ueber prefs:<user>). Default = Markup-Reihenfolge; via CSS
  // `order` umgeordnet, damit das Karten-Markup an Ort bleibt.
  const ZONE_DEFAULTS: Record<string, string[]> = {
    energie: ["energie", "wetter", "klima", "lueftung", "netzfrequenz"],
    zuhause: ["szene", "lichter", "tueren"],
  };

  // Karten fuer eigene ESPHome-Geraete entstehen von selbst: kommt ein
  // Geraet dazu (Feinstaub 19.09.), ist seine Karte da -- ohne Code.
  // Bewusst nur ESPHome: das sind die paar selbstgebauten Messgeraete,
  // deren Werte aufs Dashboard gehoeren. 34 Zigbee-Geraete als Karten
  // waeren Krach. Entfernen geht im Anordnen-Modus, zurueck kommt sie
  // ueber "Karte hinzufuegen".
  ensureDevices();
  const geraeteKarten = $derived(
    iotDevices.list
      .filter(
        (d) =>
          d.integrationSlug === "esphome" &&
          d.entities.some((e) => e.kind === "sensor") &&
          // Stillgelegtes gehoert nicht aufs Dashboard: es ist bewusst
          // weg. Wird es reaktiviert, ist die Karte wieder da.
          !(d.disableDomain && d.disableEntity && isEntityDisabled(d.disableDomain, d.disableEntity)),
      )
      .sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name, "de")),
  );

  // Die grosse Zahl der Karte ist der Leitwert des Geraets, nicht der
  // alphabetisch erste Sensor (sonst stand beim Feinstaub die
  // Partikelanzahl vorn statt PM2.5).
  const LEITWERTE = ["pm2_5", "co2", "co2_equivalent", "temperature", "humidity", "iaq"];

  // ── Was eine Geraete-Karte zeigt (Zahnrad im Anordnen-Modus) ───────
  // Vorgabe: Messwert + Liste, Leitwert nach LEITWERTE. Gespeichert
  // wird erst, was der Nutzer bewusst waehlt (card-config.ts).
  let configKarte = $state<string | null>(null);
  function sensoren(d: { entities: { kind: string; state: string; slug: string; name: string; unit: string }[] }) {
    return d.entities.filter((e) => e.kind === "sensor" && e.state !== "");
  }
  // Die Klima-Karte soll nicht mit der Sensor-Zahl mitwachsen: nur die
  // auffaelligen (max 2) plus die juengsten "zuletzt gemeldet"-Eintraege
  // bis zum Zeilen-Budget. Der Rest verbirgt sich hinter "+N weitere"
  // (volle Liste auf /zuhause/sensoren). Das Budget ist einstellbar.
  const KLIMA_MAX_ROWS = 5;

  // ── Was die FESTEN Karten zeigen ──────────────────────────────────
  // Anders als bei Geraete-Karten sind die Slugs hier keine Sensoren,
  // sondern die Bausteine der Karte: jeder abwaehlbar. So hat das
  // Zahnrad ueberall eine Wirkung -- vorher sass es auch auf diesen
  // Karten, tat aber nichts (Tim 20.09.).
  const FESTE_BAUSTEINE = $derived<Record<string, { slug: string; name: string }[]>>({
    energie: [
      { slug: "kurve", name: t("dashboard.cfg.kurve", "Verlaufskurve") },
      { slug: "heute", name: t("dashboard.cfg.energie.today", "Summe heute") },
      { slug: "monat", name: t("dashboard.cfg.energie.month", "Summe Monat") },
      { slug: "jahr", name: t("dashboard.cfg.energie.year", "Summe Jahr") },
      { slug: "kosten", name: t("dashboard.cfg.energie.cost", "Kosten in Euro") },
    ],
    wetter: [
      { slug: "gefuehlt", name: t("dashboard.cfg.wetter.feels", "Gefühlte Temperatur") },
      { slug: "feuchte", name: t("dashboard.cfg.wetter.humidity", "Feuchte") },
      { slug: "wind", name: t("dashboard.cfg.wetter.wind", "Wind") },
      { slug: "wolken", name: t("dashboard.cfg.wetter.clouds", "Wolken") },
      { slug: "vorschau", name: t("dashboard.cfg.wetter.forecast", "Stunden-Vorschau") },
    ],
    netzfrequenz: [
      { slug: "kurve", name: t("dashboard.cfg.kurve", "Verlaufskurve") },
      { slug: "tagesbereich", name: t("dashboard.cfg.nf.day", "Bereich der letzten 24 Stunden") },
    ],
    klima: [],
  });

  // Vorgaben = das, was die Karten vor dieser Einstellung zeigten.
  // Wer nichts anfasst, sieht also genau das Gewohnte.
  const FESTE_VORGABE: Record<string, CardConfig> = {
    energie: { modus: "liste", felder: ["kurve", "heute", "monat", "jahr", "kosten"], stunden: 48 },
    wetter: { modus: "liste", felder: ["gefuehlt", "feuchte", "wind", "wolken", "vorschau"], stunden: 6 },
    netzfrequenz: { modus: "liste", felder: ["kurve", "tagesbereich"], stunden: 1 },
    klima: { modus: "liste", anzahl: KLIMA_MAX_ROWS },
  };

  const stundenOpt = (h: number[]): { wert: number; label: string }[] =>
    h.map((n) => ({ wert: n, label: `${n} h` }));

  const FESTE_ZAHLEN = $derived<Record<string, ZahlAbschnitt[]>>({
    energie: [
      { feld: "stunden", titel: t("dashboard.cfg.window", "Zeitfenster der Kurve"), vorgabe: 48, optionen: stundenOpt([12, 24, 48]) },
    ],
    wetter: [
      { feld: "stunden", titel: t("dashboard.cfg.wetter.hours", "Stunden im Voraus"), vorgabe: 6, optionen: stundenOpt([3, 6, 12]) },
    ],
    netzfrequenz: [
      {
        feld: "stunden",
        titel: t("dashboard.cfg.window", "Zeitfenster der Kurve"),
        vorgabe: 1,
        // Die Quelle liefert Minutenmittel der letzten Stunde -- mehr
        // als 60 min gibt es hier nicht zu zeigen.
        optionen: [
          { wert: 0.25, label: "15 min" },
          { wert: 0.5, label: "30 min" },
          { wert: 1, label: "60 min" },
        ],
      },
    ],
    klima: [
      {
        feld: "anzahl",
        titel: t("dashboard.cfg.klima.rows", "Zeilen"),
        vorgabe: KLIMA_MAX_ROWS,
        optionen: [3, 5, 8, 99].map((n) => ({
          wert: n,
          label: n >= 99 ? t("dashboard.cfg.all", "alle") : String(n),
        })),
      },
    ],
  });

  /** Zeigt diese feste Karte den Baustein? Ohne Auswahl gilt die Vorgabe. */
  function zeigtBaustein(id: string, slug: string): boolean {
    return (kartenConfig(id).felder ?? FESTE_VORGABE[id]?.felder ?? []).includes(slug);
  }

  function kartenConfig(id: string): CardConfig {
    const feste = FESTE_VORGABE[id];
    if (feste) return getCardConfig("dashboard_energie", id, feste);
    const d = geraetVon(id);
    const werte = d ? sensoren(d) : [];
    const standard = werte[leitwert(werte)]?.slug;
    // Vorgabe bewusst knapp: die drei naechstwichtigen Werte. Alle
    // aufzulisten machte die Karte doppelt so hoch wie ihre Nachbarn
    // (Tim 20.09.); wer mehr will, waehlt sie im Zahnrad dazu.
    // Diagnose-Zähler (Laufzeit, WLAN-Signal) kommen nicht von selbst
    // dazu -- bis 03.10. füllten sie die Karte, sobald ein Gerät sonst
    // nur ein oder zwei Messwerte hatte. Im Zahnrad bleiben sie wählbar.
    const felder = werte
      .filter((e) => e.slug !== standard && !istDiagnose(e.slug, e.unit))
      .sort((a, b) => rang(a.slug) - rang(b.slug))
      .slice(0, 3)
      .map((e) => e.slug);
    return getCardConfig("dashboard_energie", id, { modus: "liste", leit: standard, felder, stunden: 24 });
  }

  // Verläufe der Karten über den Geräte-Verlauf (entity_history): je Gerät,
  // Wert und Zeitraum ein Fenster, das nur den einen Wert holt und danach
  // nur noch fortgeschrieben wird (lib/verlauf-fenster.ts).
  const fenster: Record<string, Fenster> = {};
  async function holeFenster(slug: string, entitaet: string, stunden: number): Promise<Fenster> {
    const schluessel = `${slug}|${entitaet}|${stunden}`;
    const von = new Date(Date.now() - stunden * 3600_000).toISOString();
    const ab = abrufAb(fenster[schluessel], von);
    const r = await getJSON<{ entities: ({ slug: string } & VerlaufAntwort)[] }>(
      `/v2-api/iot/devices/${encodeURIComponent(slug)}/history?from=${encodeURIComponent(ab)}&entity=${encodeURIComponent(entitaet)}`,
    );
    const e = r.entities?.find((x) => x.slug === entitaet) ?? {};
    return (fenster[schluessel] = fortschreiben(fenster[schluessel], ab, von, e));
  }

  // Verlauf des Leitwerts -- nur fuer Karten im Verlauf-Modus.
  let verlaeufe = $state<Record<string, (number | null)[]>>({});
  const verlaufStand: Record<string, number> = {};
  async function ladeVerlauf(slug: string, entitaet: string, stunden: number): Promise<void> {
    const schluessel = `${slug}|${entitaet}|${stunden}`;
    const jetzt = Date.now();
    // Nicht oefter als alle 2 min je Karte -- der Verlauf ist kein
    // Live-Wert, die grosse Zahl darueber schon.
    if (verlaufStand[schluessel] && jetzt - verlaufStand[schluessel] < 120_000) return;
    verlaufStand[schluessel] = jetzt;
    try {
      const f = await holeFenster(slug, entitaet, stunden);
      const werte = f.punkte.map((p) => (typeof p.num === "number" ? p.num : null));
      verlaeufe = { ...verlaeufe, [schluessel]: werte };
    } catch {
      // still: die Karte zeigt dann nur den Messwert.
    }
  }
  $effect(() => {
    for (const d of geraeteKarten) {
      const id = `geraet:${d.slug}`;
      const cfg = kartenConfig(id);
      if (cfg.modus !== "verlauf" || !cfg.leit) continue;
      void ladeVerlauf(d.slug, cfg.leit, cfg.stunden ?? 24);
    }
  });
  // ── Karte „Lüftung & Heizung" (lib/dashboard/lueftung.ts) ──────────
  // Die drei Fühler über ihren Namen; je Fühler der Temperatur-Verlauf der
  // letzten 24 h. Neu geholt, sobald einer der drei neu misst -- höchstens
  // alle 2 min (wie ladeVerlauf).
  const lueftungFuehler = $derived(
    LUEFTUNG_FUEHLER.map((f) => {
      const d = iotDevices.list.find((x) => x.name === f.name);
      const temp = d?.entities.find((e) => e.slug === "temperature");
      const wert = temp ? Number.parseFloat(temp.state) : Number.NaN;
      return { ...f, slug: d?.slug, wert: Number.isFinite(wert) ? wert : null, zuletzt: temp?.updatedAt ?? "" };
    }),
  );
  let lueftungVerlauf = $state<Record<string, Messpunkt[]>>({});
  let lueftungStand = 0;
  async function ladeLueftung(): Promise<void> {
    const jetzt = Date.now();
    // Erst wenn die Geräte da sind: ein Lauf ins Leere sperrte sonst die
    // nächsten 2 min, und die Karte blieb nach dem Laden ohne Kurve.
    if (!lueftungFuehler.some((f) => f.slug)) return;
    if (jetzt - lueftungStand < 120_000) return;
    lueftungStand = jetzt;
    const von = new Date(jetzt - 24 * 3600_000).toISOString();
    const neu: Record<string, Messpunkt[]> = {};
    for (const f of lueftungFuehler) {
      if (!f.slug) continue;
      try {
        const e = await holeFenster(f.slug, "temperature", 24);
        const punkte = [...(e.prior ? [{ ...e.prior, ts: von }] : []), ...e.punkte];
        neu[f.schluessel] = punkte
          .filter((p) => typeof p.num === "number")
          .map((p) => ({ t: Math.floor(Date.parse(p.ts) / 1000), v: p.num as number }));
      } catch {
        // still: die Karte zeigt dann die Werte ohne Kurve.
      }
    }
    lueftungVerlauf = neu;
  }
  $effect(() => {
    // folgt jeder neuen Messung der drei Fühler
    void lueftungFuehler.map((f) => f.zuletzt).join("|");
    void ladeLueftung();
  });
  const lueftung = $derived.by(() => {
    const bis = Math.floor(now.getTime() / 1000);
    const reihen = LUEFTUNG_FUEHLER.map((f) => lueftungVerlauf[f.schluessel] ?? []);
    const heizung = lueftungVerlauf.heizung ?? [];
    const jetztHeizung = lueftungFuehler.find((f) => f.schluessel === "heizung")?.wert ?? null;
    return {
      daten: aufEineAchse(reihen, bis),
      zonen: heizPhasen(heizung, bis),
      lage: lueftungLage(lueftungVerlauf.aussen ?? [], heizung, jetztHeizung, bis),
    };
  });

  /** Rang eines Sensors: je kleiner, desto wichtiger. */
  function rang(slug: string): number {
    const i = LEITWERTE.indexOf(slug);
    return i < 0 ? 99 : i;
  }
  function leitwert(werte: { slug: string }[]): number {
    for (const name of LEITWERTE) {
      const i = werte.findIndex((e) => e.slug === name);
      if (i >= 0) return i;
    }
    return 0;
  }
  /** Symbol einer Geräte-Karte nach ihrem Leitwert (docs/symbole.md D30):
   *  `wind` heißt Luft, nicht „irgendein Messgerät“. */
  function leitSymbol(slug: string | undefined): IconName {
    if (slug === "temperature") return "thermo";
    if (slug === "humidity") return "droplet";
    return "wind";
  }
  const geraeteIds = $derived(geraeteKarten.map((d) => `geraet:${d.slug}`));
  // Mit den Werten aus dem Protokoll, wo ein frischer Stand da ist (sonst
  // bleibt der MQTT-Zustand): die Karte sieht dieselbe Gestalt wie vorher.
  function geraetVon(id: string) {
    const d = geraeteKarten.find((g) => `geraet:${g.slug}` === id);
    if (!d) return null;
    const entities = feinstaubEntitaeten(d.haDeviceId, d.entities, () => frischeMs);
    return entities === d.entities ? d : { ...d, entities };
  }
  // Klarnamen der festen Karten -- ohne sie stand im Dialog und in
  // "Karte hinzufügen" der rohe Schlüssel ("energie").
  const KARTEN_NAMEN = $derived<Record<string, string>>({
    energie: t("dashboard.card.energie", "Energie · jetzt"),
    wetter: t("dashboard.card.wetter", "Wetter"),
    klima: t("dashboard.card.klima", "Klima"),
    lueftung: t("dashboard.card.lueftung", "Lüftung & Heizung"),
    netzfrequenz: t("dashboard.card.netzfrequenz", "Netzfrequenz"),
    szene: t("dashboard.card.szene", "Szenen"),
    lichter: t("dashboard.card.lichter", "Lichter"),
    tueren: t("dashboard.card.tueren", "Türen & Fenster"),
  });
  function kartenTitel(id: string): string {
    if (!id.startsWith("geraet:")) {
      return KARTEN_NAMEN[id] ?? t(`dashboard.card.${id}`, id);
    }
    const d = geraetVon(id);
    return d ? d.label || d.name : id;
  }
  // Anordnen-Modus je Zone -- CardGrid bindet sich hierauf; die
  // arrangeBar (Reset/Fertig) in den Zonen-Headern liest/setzt ihn.
  // Geoeffnet wird der Modus per Long-Press auf eine Karte (CardGrid),
  // beendet per Klick-ausserhalb/Esc (CardGrid) oder "Fertig".
  const zoneArranging = $state<Record<string, boolean>>({
    energie: false,
    zuhause: false,
  });

  // Reset: Spalten-Layout (Desktop) + Reihenfolge (Mobil) der Zone
  // verwerfen -> zurueck auf die Default-Verteilung.
  function resetZone(group: string): void {
    resetLayout(`dashboard_${group}`);
    resetCardOrder(`dashboard_${group}`);
  }

  // WAN: Internet-Probe (1.1.1.1:443) + Agent-fed Felder (IPv4/IPv6/Bandbreite)
  // des Routers hinter forge -- der Hauptwert der Karte. Jeder weitere
  // Router (seit 25.10.2026: zuhause) steht als eigene Zeile darunter,
  // mit dem, was sein Agent meldet; die Werte springen nie zwischen den
  // Anschlüssen.
  const wan = $derived(getWan());
  const wanWeitere = $derived(weitereRouter(wan).map(routerKurz));
  const wanHier = $derived(probeRouter(wan));

  // Storage: aggregiert ueber alle Pools (% belegt, busiest pool).
  const storage = $derived(getStorageSummary());

  // Wetter: aktueller Snapshot von Open-Meteo (cache 15 min im Backend).
  const weather = $derived(getWeatherSummary());

  // 6h-Forecast ab naechster voller Stunde. OpenMeteo-Times sind
  // lokale ISO-Strings ohne TZ ("2026-05-09T15:00") -- new Date()
  // interpretiert die als Local Time, was im Browser des Nutzers
  // exakt mit der Berlin-TZ-Anfrage des Backends matched.
  const forecastSlots = $derived.by(() => {
    if (!weather?.hourly) return [];
    const h = weather.hourly;
    const next = new Date(now);
    next.setMinutes(0, 0, 0);
    next.setHours(next.getHours() + 1);
    const idx = h.times.findIndex((t) => new Date(t).getTime() >= next.getTime());
    if (idx < 0) return [];
    const slots: { time: string; temp: number; code: number; precip: number; isDay: number }[] = [];
    const wieviele = Math.round(kartenConfig("wetter").stunden ?? 6);
    const end = Math.min(idx + wieviele, h.times.length);
    for (let i = idx; i < end; i++) {
      const hour = parseInt(h.times[i].slice(11, 13), 10);
      slots.push({
        time: h.times[i],
        temp: h.temps[i],
        code: h.codes[i],
        precip: h.precip[i] ?? 0,
        isDay: hour >= 7 && hour < 20 ? 1 : 0,
      });
    }
    return slots;
  });

  // Agents: registrierte Edge-Komponenten (Heartbeat-Tracking, Phase A).
  const agentList = $derived(getAgents());
  const agentSummary = $derived.by(() => {
    let ok = 0, stale = 0, neverSeen = 0, disabled = 0, clusterDisabled = 0;
    for (const a of agentList) {
      if (a.status === "ok") ok++;
      else if (a.status === "stale") stale++;
      else if (a.status === "never_seen") neverSeen++;
      else if (a.status === "disabled") disabled++;
      else if (a.status === "cluster_disabled") clusterDisabled++;
    }
    // cluster_disabled zaehlt bewusst NICHT als Problem -- ein stillgelegtes
    // Cluster ist ein gewollter Zustand, kein Alarm (roter Punkt bleibt aus).
    const status: "ok" | "problem" = (stale + neverSeen) > 0 ? "problem" : "ok";
    return { total: agentList.length, ok, stale, neverSeen, disabled, clusterDisabled, status };
  });

  // Hosts: Online-Zahl fuer den Chip "Infrastruktur" im System-Streifen.
  const hosts = $derived(getHostsSummary());

  // Sensoren / Aktoren / Automatisierungen / Integrationen: Daten-
  // Stores. Die alte navTile-basierte Card-Berechnung ist seit dem
  // FavoriteTile-Refactor durch sensorenFav/aktorenFav/etc. ersetzt
  // (weiter unten); hier bleiben nur die rohen Stores.
  const klima = $derived(getKlimaSummary());
  const aktoren = $derived(getAktorenSummary());
  const energy = $derived(getEnergySummary());

  const ambient = $derived<AmbientView[]>(getAmbient());

  // ── Dashboard-spezifische Aggregate (Bento-Layout) ─────────────


  // Klima-Karte: nur die fuer "Klima" relevanten Sensoren (climate +
  // co2). Tueren/Bewegung/Wasser/Lipo gehoeren in die Sensoren-Sektion.
  const klimaAlle = $derived.by(() => {
    if (!klima) return [];
    return klima.sensors.filter(
      (s) => (s.domain === "climate" || s.domain === "co2") && !s.disabled && !isEntityDisabled(s.domain, s.sensor_id),
    );
  });
  // Abgewaehlte Sensoren (Zahnrad) kommen auf der Karte gar nicht mehr
  // vor -- auch nicht in "+N weitere" und nicht im Zaehler.
  const klimaSensoren = $derived.by(() => {
    const gewaehlt = kartenConfig("klima").felder;
    if (!gewaehlt) return klimaAlle;
    return klimaAlle.filter((s) => gewaehlt.includes(s.sensor_id));
  });
  // Auffaellig oben: Sensoren die aus dem Bereich laufen (warn/problem).
  const klimaAuffaellig = $derived.by(() => {
    return klimaSensoren.filter((s) => s.status === "warning" || s.status === "problem");
  });
  // Zuletzt gemeldet: alle aktiven Klima-Sensoren absteigend nach ts.
  const klimaLetzte = $derived.by(() => {
    return [...klimaSensoren].sort((a, b) => (b.ts ?? "").localeCompare(a.ts ?? ""));
  });
  const klimaAuffaelligShown = $derived(klimaAuffaellig.slice(0, 2));
  const klimaRest = $derived(
    klimaLetzte.filter((x) => !klimaAuffaelligShown.includes(x)),
  );
  const klimaZeilen = $derived(kartenConfig("klima").anzahl ?? KLIMA_MAX_ROWS);
  const klimaRecentShown = $derived(
    klimaRest.slice(0, Math.max(1, klimaZeilen - klimaAuffaelligShown.length)),
  );
  const klimaHiddenCount = $derived(klimaRest.length - klimaRecentShown.length);
  // Primaer-Wert fuer einen Sensor: temperature fuer climate, co2 fuer
  // CO2-Sensor.
  // klimaValue liefert NUR die Zahl (ohne Einheit), damit das Markup
  // sie in einem fixed-width-Slot rechtsbuendig rendern und die Einheit
  // separat in einem links-buendigen Slot platzieren kann. Damit liegen
  // die Ziffern unterschiedlich langer Werte (z.B. "1013" ppm vs "23.2"
  // °) untereinander in derselben Spalte.
  function klimaValue(s: { domain: string; metrics: Record<string, number> }): string {
    if (s.domain === "climate") {
      const t = s.metrics?.temperature;
      return typeof t === "number" ? formatZahl(t) : "—";
    }
    if (s.domain === "co2") {
      const v = s.metrics?.co2_ppm;
      return typeof v === "number" ? `${Math.round(v)}` : "—";
    }
    return "—";
  }
  function klimaUnit(s: { domain: string }): string {
    if (s.domain === "co2") return "ppm";
    if (s.domain === "climate") return "°C";
    return "";
  }

  // Aktive Szene-Block (aus ambient)
  const ambientPrimary = $derived<AmbientView | undefined>(ambient[0]);
  const ambientHeader = $derived(
    ambientPrimary
      ? `${t("dashboard.section.scene_active", "Aktive Szene")} · ${t("dashboard.section.phase", "Phase")} ${ambientPrimary.phase_label}`
      : t("dashboard.section.scene_idle", "Szenen"),
  );
  // Headline: bevorzugt Name der aktiven Szene (egal ob Custom oder
  // Restore -- siehe SceneActiveState im Backend, beide setzen active=true
  // im SceneView). Wenn keine Szene aktiv ist und Ambient an: einfach
  // den Phase-Namen ("Tag" / "Abend" / ...). Aus = "Aus".
  const ambientSzene = $derived.by(() => {
    const activeFromScenes = scenes.find((s) => s.active)?.name;
    if (activeFromScenes) return activeFromScenes;
    if (ambientPrimary?.on) return ambientPrimary.phase_label;
    return t("dashboard.section.scene_off", "Aus");
  });
  const ambientSummary = $derived.by(() => {
    if (!ambientPrimary) return "";
    const phase = ambientPrimary.phases?.[ambientPrimary.current_phase];
    const br = phase?.brightness ?? 0;
    const pct = Math.round((br / 254) * 100);
    return `${ambientPrimary.name} · ${pct} %`;
  });
  // Scenes-Liste fuer die Buttons -- echte Daten aus dem Store, mit
  // dem `active`-Flag aus dem Backend (siehe SceneView). Klick auf
  // einen Button aktiviert die Scene; Klick auf die Karte selbst
  // springt zur Lichteinstellung.
  const scenes = $derived<SceneView[]>(getScenes());
  const scenesForButtons = $derived.by<SceneView[]>(() => {
    return scenes.filter((s) => s.enabled && s.on_go_engine).slice(0, 4);
  });

  let activatingScene = $state<string | null>(null);
  async function activateScene(name: string) {
    if (activatingScene) return;
    activatingScene = name;
    try {
      // Klick auf eine bereits aktive Szene = Lichter aus (Master-Off
      // auf der primaeren Ambient-Gruppe). So fungiert die Pille als
      // Toggle ohne extra "Aus"-Button pro Szene.
      const isActive = scenes.find((s) => s.name === name)?.active === true;
      if (isActive && ambientPrimary) {
        await postJSON(`/v2-api/ambient/${encodeURIComponent(ambientPrimary.name)}/master-off`, {});
      } else {
        await postJSON(`/v2-api/automations/${encodeURIComponent(name)}/activate`, {});
      }
      // Scene-State im Backend wird async im engine.SceneActiveState
      // sichtbar; ein refresh nudge nach 500ms zieht das frische Bild.
      setTimeout(() => void refreshAmbient(), 500);
    } catch (e) {
      // Fehler am Ort melden, nicht nur in die Konsole (Repo-Prüfung
      // 21.10.2026, zweite Runde): sonst blieb die Pille stumm stehen.
      toast.error(`${name}: ${e instanceof APIError ? e.detail : e instanceof Error ? e.message : t("dashboard.szene.fehler", "Szene ließ sich nicht schalten")}`);
    } finally {
      activatingScene = null;
    }
  }

  // Alles-aus: Master-Off auf alle ambient_groups + alle ON-Lichter in
  // aktoren parallel ausschalten. Bewusster Kill-Switch von der
  // Zuhause-Karte aus, damit man nicht erst zu /zuhause/aktoren navigieren
  // muss.
  let allOffPending = $state(false);
  async function allOff(): Promise<void> {
    if (allOffPending) return;
    allOffPending = true;
    try {
      const calls: { name: string; p: Promise<unknown> }[] = [];
      for (const a of ambient) {
        if (a.on) {
          calls.push({ name: a.name, p: postJSON(`/v2-api/ambient/${encodeURIComponent(a.name)}/master-off`, {}) });
        }
      }
      if (aktoren) {
        for (const d of aktoren.devices) {
          if (d.kind === "light" && d.state === "ON" && d.available !== false) {
            calls.push({ name: d.name, p: postJSON(`/v2-api/zigbee/toggle/${encodeURIComponent(d.name)}`, { state: "OFF" }) });
          }
        }
      }
      // Was nicht ausging, beim Namen nennen. Bis 21.10.2026 wurde das
      // Ergebnis nicht ausgewertet -- „Alles aus" scheiterte stumm
      // (Repo-Prüfung, zweite Runde).
      const erg = await Promise.allSettled(calls.map((c) => c.p));
      const fehl = calls.filter((_, i) => erg[i].status === "rejected").map((c) => c.name);
      if (fehl.length > 0) {
        toast.error(
          t("dashboard.alles_aus.fehler", "{n} von {von} Lichtern nicht ausgeschaltet: {namen}")
            .replace("{n}", String(fehl.length))
            .replace("{von}", String(calls.length))
            .replace("{namen}", fehl.join(", ")),
        );
      }
      setTimeout(() => {
        void refreshAmbient();
        void refreshAktorenSummary();
      }, 300);
    } finally {
      allOffPending = false;
    }
  }

  // Quick-Toggles fuer Bad + Storage in der Aktive-Szene-Karte. Liste
  // bewusst klein und hartkodiert -- die Karte ist Zuhause-Cockpit, nicht
  // Aktoren-Index.
  const quickLightDefs: { name: string; label: string }[] = [
    { name: "Light Bathroom", label: "Bad" },
    { name: "Light Storage", label: "Storage" },
  ];
  const quickLights = $derived.by(() => {
    if (!aktoren) return [];
    return quickLightDefs
      .map((def) => {
        const dev = aktoren.devices.find((d) => d.name === def.name);
        return dev
          ? { ...def, on: dev.state === "ON", available: dev.available !== false }
          : null;
      })
      .filter((x): x is { name: string; label: string; on: boolean; available: boolean } => x !== null);
  });
  let togglingLight = $state<string | null>(null);
  async function toggleQuickLight(name: string, on: boolean): Promise<void> {
    if (togglingLight) return;
    togglingLight = name;
    try {
      await postJSON(`/v2-api/zigbee/toggle/${encodeURIComponent(name)}`, {
        state: on ? "OFF" : "ON",
      });
      setTimeout(() => void refreshAktorenSummary(), 300);
    } catch (e) {
      toast.error(`${name}: ${e instanceof APIError ? e.detail : e instanceof Error ? e.message : t("aktoren.toast.toggle_failed", "Toggle fehlgeschlagen")}`);
    } finally {
      togglingLight = null;
    }
  }

  // Lichter-Aggregat aus aktoren-summary
  const lichterAn = $derived.by(() => {
    if (!aktoren) return 0;
    return aktoren.devices.filter((d) => d.kind === "light" && d.state === "ON").length;
  });
  const lichterGesamt = $derived.by(() => {
    if (!aktoren) return 0;
    return aktoren.devices.filter((d) => d.kind === "light").length;
  });
  const lichterAvgPct = $derived.by<number | null>(() => {
    if (!aktoren) return null;
    const an = aktoren.devices.filter((d) => d.kind === "light" && d.state === "ON" && typeof d.brightness === "number");
    if (an.length === 0) return null;
    const sum = an.reduce((acc, d) => acc + (d.brightness ?? 0), 0);
    return Math.round((sum / an.length) / 254 * 100);
  });
  const lichterLastTs = $derived.by<string | null>(() => {
    if (!aktoren) return null;
    const lights = aktoren.devices.filter((d) => d.kind === "light");
    if (lights.length === 0) return null;
    const sorted = [...lights].sort((a, b) => (b.last_seen ?? "").localeCompare(a.last_seen ?? ""));
    return sorted[0].last_seen ?? null;
  });

  // Tueren: alle door-Sensoren, offene zuerst (interessanter), dann
  // nach jungster Aenderung. Klima-Karte zeigt Temperaturen, hier
  // klassisches "ist alles zu" -- keine Doppelung mehr mit Klima.
  //
  // Welche Tuer die Haustuer ist, sagt das Backend (`aussen`, Einstellung
  // front_door_sensor) -- bis 04.10.2026 stand der Name hier fest im Code.
  // Nur sie wird gelb, wenn sie offen ist; steht sie laenger offen als
  // `warn_after_s`, wird sie rot (und das Backend schickt einen Push).
  // Bad/Storage offen ist normal: Lueften, Auf-/Abladen.
  const tueren = $derived.by(() => {
    if (!klima) return [];
    return klima.sensors
      .filter((s) => s.domain === "door" && !s.disabled && !isEntityDisabled(s.domain, s.sensor_id))
      .sort((a, b) => {
        const aOffen = (a.metrics?.contact ?? 1) === 0;
        const bOffen = (b.metrics?.contact ?? 1) === 0;
        const aAlarm = aOffen && a.aussen === true;
        const bAlarm = bOffen && b.aussen === true;
        if (aAlarm !== bAlarm) return aAlarm ? -1 : 1;
        if (aOffen !== bOffen) return aOffen ? -1 : 1;
        return (b.ts ?? "").localeCompare(a.ts ?? "");
      });
  });
  const tuerenOffenCount = $derived(
    tueren.filter((s) => (s.metrics?.contact ?? 1) === 0).length,
  );
  const tuerenAlarmingOffen = $derived(
    tueren.filter((s) => s.aussen === true && (s.metrics?.contact ?? 1) === 0).length,
  );

  // Eine Tür, wie Karte und Lage-Satz sie brauchen.
  function tuerVon(s: { sensor_id: string; metrics: Record<string, number>; since?: string; aussen?: boolean; warn_after_s?: number }): Tuer {
    const offen = (s.metrics?.contact ?? 1) === 0;
    return {
      name: s.sensor_id,
      offen,
      wichtig: s.aussen === true,
      // `now` tickt jede Sekunde: die Zeile wird genau dann rot, wenn die
      // Schwelle erreicht ist, nicht erst beim naechsten Abruf.
      zuLang: s.aussen === true && offenZuLang(offen, s.since, s.warn_after_s, now.getTime()),
    };
  }
  const tuerZuLang = $derived(tueren.some((s) => tuerVon(s).zuLang));

  // ── Lage-Sätze der Zonen (Rechnung: lib/dashboard/zonen.ts) ────────
  const energieLage = $derived<LageTeil[]>(
    klima
      ? klimaLage(
          klimaAuffaellig.map((s) => ({ name: s.sensor_id, kritisch: s.status === "problem" })),
          getNetzfrequenz()?.status,
        )
      : [],
  );
  const zuhauseLageTeile = $derived<LageTeil[]>(
    aktoren || klima ? zuhauseLage(lichterAn, lichterGesamt, tueren.map(tuerVon)) : [],
  );

  void router; // keep router referenced

  // ── System-Streifen: EINE Stelle fuer offene Probleme ──────────────
  //
  // Bis 13.09.2026 stand derselbe Vorfall dreimal auf dem Dashboard: ein
  // rotes Kritisch-Banner, "1 Punkt offen" im Streifen und eine Problem-
  // Karte darunter. Der Streifen zaehlte dabei eine eigene, hier fest
  // verdrahtete Problemliste -- eine zweite Wahrheit neben den Meldungen.
  // Seit die Meldungen alles tragen (auch Kritisches), ist der Streifen
  // nur noch ihr Kopf: die schwerste offene Meldung, wie viele es noch
  // gibt, und ein Klick oeffnet sie (Tim: "Wir haben ja jetzt die
  // Meldungen"). Hinweise zaehlen mit, faerben aber nichts ein.
  const offeneMeldungen = $derived(getAlertsVisible());
  const probleme = $derived(offeneMeldungen.filter((a) => a.severity !== "info"));
  const hinweise = $derived(offeneMeldungen.length - probleme.length);

  // Massnahmen gibt es nur an kritischen Ereignissen (critical-summary
  // loest das stummschaltbare Cluster auf).
  const kopfMassnahmen = $derived.by((): ReturnType<typeof remediationsForCritical> => {
    const top = probleme[0];
    if (!top || top.severity !== "critical" || top.eventId == null) return [];
    const ev = criticalEvents.find((e) => e.id === top.eventId);
    return ev ? remediationsForCritical(ev.remediation_cluster) : [];
  });

  // Kopf der Statuszeile: Verbindungszustand des GERAETS schlaegt alles
  // (eingefrorene Daten), danach die dringendste offene Meldung.
  const systemLead = $derived.by(() => {
    const topologie = { href: "#/infra/topologie", title: t("dashboard.sys.topology_title", "Zur Topologie — der Überblick") };
    if (connState === "no_net") {
      return {
        sev: "warn" as const,
        label: t("dashboard.sys.lead_no_net", "Gerät offline (WLAN?)"),
        sub: t("dashboard.sys.lead_no_net_sub", "Anzeige eingefroren — der Server ist unbeteiligt"),
        ...topologie,
      };
    }
    if (connState === "server_unreachable" || hbStale) {
      return {
        sev: "danger" as const,
        label: t("dashboard.system.server_gone_line", "Server nicht erreichbar"),
        sub: t("dashboard.sys.lead_server_gone_sub", "kein Heartbeat — Anzeige eingefroren"),
        ...topologie,
      };
    }
    const top = probleme[0];
    if (!top) {
      return {
        sev: "ok" as const,
        label: t("dashboard.sys.lead_calm", "Alles ruhig"),
        sub:
          hinweise === 0
            ? t("dashboard.sys.lead_calm_sub", "keine offenen Meldungen")
            : hinweise === 1
              ? t("dashboard.sys.lead_hint_one", "1 Hinweis offen")
              : `${hinweise} ${t("dashboard.sys.lead_hints", "Hinweise offen")}`,
        ...(hinweise > 0
          ? { href: "#/system/meldungen", title: t("dashboard.sys.lead_to_alerts", "Zu den Meldungen") }
          : topologie),
      };
    }
    const weitere = offeneMeldungen.length - 1;
    const stufe = SEVERITY_TEXT[top.severity];
    return {
      sev: top.severity === "warning" ? ("warn" as const) : ("danger" as const),
      label: top.title,
      sub:
        t(stufe.key, stufe.fallback) +
        (weitere > 0
          ? ` · +${weitere} ${weitere === 1 ? t("dashboard.sys.lead_more_one", "weitere Meldung") : t("dashboard.sys.lead_more", "weitere Meldungen")}`
          : ""),
      href: `#/system/meldungen?open=${encodeURIComponent(top.id)}`,
      title: t("dashboard.sys.lead_open", "Meldung aufklappen"),
    };
  });

  // Lage der System-Zone. Ruhig: ein Wort, dazu offene Hinweise. Sonst
  // sagt der Kopf, WIE VIEL offen ist -- welche Meldung die dringendste
  // ist, steht im Balken darunter.
  const systemLage = $derived.by<LageTeil[]>(() => {
    if (systemLead.sev === "ok") {
      const teile: LageTeil[] = [{ text: t("dashboard.sys.lage_calm", "alles ruhig"), ton: "ok" }];
      if (hinweise > 0) teile.push({ text: systemLead.sub });
      return teile;
    }
    if (probleme.length === 0 || connState === "no_net" || connState === "server_unreachable" || hbStale) {
      return [{ text: t("dashboard.sys.lage_frozen", "Anzeige eingefroren"), ton: systemLead.sev }];
    }
    const n = offeneMeldungen.length;
    return [
      {
        text: n === 1 ? t("dashboard.sys.lage_one", "1 Meldung offen") : `${n} ${t("dashboard.sys.lage_many", "Meldungen offen")}`,
        ton: systemLead.sev,
      },
    ];
  });

  // Kennzahlen der System-Zone: was davon gerade ein Befund ist.
  const infraAuffaellig = $derived(
    (hosts?.hosts_offline ?? 0) > 0 || agentSummary.stale + agentSummary.neverSeen > 0 || uptimeCard.down.length > 0,
  );
  const tasksRot = $derived(tasks?.currently_failing_unacked ?? 0);
  const wanTon = $derived(wan?.status === "problem" ? "text-danger" : wan?.status === "warning" ? "text-warning" : "");
  const storageTon = $derived(storage?.status === "problem" ? "danger" : storage?.status === "warning" ? "warning" : "");
</script>

{#snippet dashboardInfo()}
  <p class="text-fg text-sm leading-relaxed">
    {t(
      "dashboard.info.what",
      "Homelab-Dashboard v2 — alles Wichtige auf einen Blick: Hosts, Sicherheit, Tasks, Sensoren und Aktoren.",
    )}
  </p>
  {#if versionInfo}
    <dl
      class="text-fg-muted grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 pt-2 text-xs"
    >
      <dt class="text-fg-subtle">Version</dt>
      <dd class="font-mono">{versionInfo.version}</dd>
      <dt class="text-fg-subtle">Commit</dt>
      <dd class="font-mono">{versionInfo.commit}</dd>
      <dt class="text-fg-subtle">Build</dt>
      <dd class="font-mono">{versionInfo.build_time}</dd>
      <dt class="text-fg-subtle">Go</dt>
      <dd class="font-mono">{versionInfo.go_version}</dd>
      <dt class="text-fg-subtle">Node</dt>
      <dd class="font-mono">{stackVersions.node}</dd>
      <dt class="text-fg-subtle">Svelte</dt>
      <dd class="font-mono">{stackVersions.svelte}</dd>
      <dt class="text-fg-subtle">Vite</dt>
      <dd class="font-mono">{stackVersions.vite}</dd>
      <dt class="text-fg-subtle">TypeScript</dt>
      <dd class="font-mono">{stackVersions.typescript}</dd>
      <dt class="text-fg-subtle">Tailwind</dt>
      <dd class="font-mono">{stackVersions.tailwindcss}</dd>
    </dl>
  {:else}
    <p class="text-fg-subtle text-xs">lade Versionsdaten…</p>
  {/if}
{/snippet}

<!-- Modus-Leiste pro Zone: erscheint NUR waehrend des Sortierens (per
     Long-Press auf eine Karte geoeffnet). data-arrange-keep haelt den
     Klick-ausserhalb-beendet-Handler davon ab, die Leiste als
     "ausserhalb" zu werten. Zuruecksetzen als Symbol, "Fertig"
     beschriftet -- wie die Werkzeuge im Kopf einer Tabelle. -->
{#snippet arrangeBar(group: string)}
  {#if zoneArranging[group]}
    <div data-arrange-keep class="flex items-center gap-1">
      <SymbolKnopf
        icon="rotate-ccw"
        label={t("dashboard.arrange.reset_title", "Reihenfolge dieser Zone zurücksetzen")}
        onclick={() => resetZone(group)}
      />
      <button
        type="button"
        onclick={() => (zoneArranging[group] = false)}
        class="ctrl-s border-accent/50 bg-accent/10 text-accent shrink-0 rounded-lg border t-meta transition"
      >
        <Icon name="check" size={13} />
        {t("dashboard.arrange.done", "Fertig")}
      </button>
    </div>
  {/if}
{/snippet}

<!-- Kopf einer Karte: Symbol in der Farbe des Bereichs, in den die Karte
     führt, und ihr Name. Rechts daneben steht je Karte ihr Zustand. -->
{#snippet kartenKopf(icon: IconName, titel: string)}
  <span class="text-fg-muted flex min-w-0 items-center gap-1.5">
    <Icon name={icon} size={14} class="bereich-symbol shrink-0" />
    <span class="t-eyebrow truncate">{titel}</span>
  </span>
{/snippet}

<!-- Kopf einer Kennzahl. „ok" steht nur mit „Zustand zeigen: alles" da
     (nurAlle) -- im Normalfall sagt die ruhige Zahl genug. -->
{#snippet kennzahlKopf(icon: IconName, titel: string, ok: boolean)}
  <span class="mb-1 flex items-center justify-between gap-2">
    {@render kartenKopf(icon, titel)}
    {#if ok}<StatusBadge tone="ok" nurAlle>{t("dashboard.kpi.ok", "ok")}</StatusBadge>{/if}
  </span>
{/snippet}

{#snippet energieCard(id: string)}
  {#if id.startsWith("geraet:")}
    {@const d = geraetVon(id)}
    {#if d}
      {@const alle = sensoren(d)}
      {@const cfg = kartenConfig(id)}
      {@const haupt = alle.find((e) => e.slug === cfg.leit) ?? alle[leitwert(alle)]}
      {@const gewaehlt = cfg.felder ?? alle.map((e) => e.slug)}
      {@const werte = alle.filter((e) => e.slug !== haupt?.slug && gewaehlt.includes(e.slug)).slice(0, 6)}
      {@const kurve = verlaeufe[`${d.slug}|${cfg.leit ?? ""}|${cfg.stunden ?? 24}`] ?? []}
      <a href="#/zuhause/geraete" class="surface border border-border rounded-xl px-4 py-3.5 card-hover shadow-sm transition block">
        <div class="flex items-center justify-between gap-2 mb-1">
          {@render kartenKopf(leitSymbol(haupt?.slug), d.label || d.name)}
          {#if d.online === false}
            <StatusBadge tone="danger">{t("dashboard.geraet.offline", "nicht erreichbar")}</StatusBadge>
          {/if}
        </div>
        {#if haupt}
          {@const hw = messwert(haupt.state, haupt.unit)}
          <div class="flex items-baseline gap-2">
            <span class="t-kpi tabular-nums">{hw.wert}</span>
            <span class="text-fg-muted t-headline">{hw.einheit}</span>
            <span class="text-fg-muted t-meta ml-1 truncate">{haupt.name}</span>
          </div>
          {#if cfg.modus === "verlauf"}
            <div class="mt-2">
              {#if kurve.length > 1}
                <SparkLine values={kurve} height={38} fluid fill platz={REIHE[messgroesse(haupt.slug)]} />
                <div class="text-fg-muted mt-0.5 flex justify-between t-meta">
                  <span>−{cfg.stunden ?? 24} h</span><span>{t("dashboard.geraet.now", "jetzt")}</span>
                </div>
              {:else}
                <p class="text-fg-muted t-meta">{t("dashboard.geraet.no_history", "noch kein Verlauf")}</p>
              {/if}
            </div>
          {/if}
          <!-- Die aktuellen Werte stehen IMMER dabei (Tim 20.09.): ein
               Verlauf beantwortet "wie war es", nicht "wie ist es". Nur
               der Modus "Nur Messwert" laesst sie bewusst weg. -->
          {#if cfg.modus !== "wert" && werte.length > 0}
            <div class="mt-2 grid gap-0.5">
              {#each werte as e (e.slug)}
                {@const w = messwert(e.state, e.unit)}
                <div class="flex items-baseline justify-between gap-2 t-meta">
                  <span class="text-fg-muted truncate">{e.name}</span>
                  <span class="text-fg tabular-nums shrink-0">{w.wert}{w.einheit ? ` ${w.einheit}` : ""}</span>
                </div>
              {/each}
            </div>
          {/if}
        {:else}
          <p class="text-fg-muted t-meta">{t("dashboard.geraet.no_values", "noch keine Werte")}</p>
        {/if}
        {#if d.lastSeen}
          <p class="text-fg-subtle t-meta mt-2">
            {t("dashboard.geraet.last", "zuletzt")} {formatRelative(d.lastSeen, now)}
          </p>
        {/if}
      </a>
    {/if}
  {:else if id === "energie"}
      {@const eCfg = kartenConfig("energie")}
      {@const eStunden = eCfg.stunden ?? 48}
      {@const eSummen = ["heute", "monat", "jahr"].filter((s) => zeigtBaustein("energie", s))}
      {@const eKosten = zeigtBaustein("energie", "kosten")}
      <a href="#/zuhause/energy" class="surface border border-border rounded-xl px-4 py-3.5 card-hover shadow-sm transition block">
        <div class="flex items-center justify-between gap-2 mb-1">
          {@render kartenKopf("zap", `${t("dashboard.energie.title", "Energie")} · ${t("dashboard.energie.now", "jetzt")}`)}
          {#if energy && energy.reporting > 0}
            <StatusBadge tone="ok">{t("dashboard.live", "live")}</StatusBadge>
          {/if}
        </div>
        <div class="flex items-baseline gap-2">
          <span class="t-kpi tabular-nums">{energy ? formatZahl(energy.total_watts) : "—"}</span>
          <span class="text-fg-muted t-headline">W</span>
          {#if energy && energy.reporting > 0}
            <span class="text-fg-subtle t-meta ml-2">{energy.reporting} {energy.reporting === 1 ? t("dashboard.energie.device", "Gerät") : t("dashboard.energie.devices", "Geräte")}</span>
          {/if}
        </div>
        {#if energy?.spark && zeigtBaustein("energie", "kurve")}
          <!-- Die Quelle liefert immer 48 h in gleich langen Buckets; ein
               kuerzeres Fenster ist der hintere Ausschnitt davon, keine
               zweite Abfrage. -->
          {@const alleWerte = energy.spark.values}
          {@const werte = alleWerte.slice(Math.max(0, alleWerte.length - Math.round((alleWerte.length * eStunden) / 48)))}
          <div class="mt-2">
            <SparkLine values={werte} fluid height={28} gridLines={3} platz={REIHE.leistung} />
            <!-- 9px absichtlich kleiner als t-meta -- die SparkLine ist nur
                 28px hoch, eine Achsen-Beschriftung in voller Meta-Groesse
                 wuerde optisch dominieren. -->
            <div class="flex justify-between text-fg-subtle text-[9px] mt-0.5 tabular-nums">
              {#each [4, 3, 2, 1] as teil (teil)}
                <span>−{Math.round((eStunden * teil) / 4)} h</span>
              {/each}
              <span>{t("dashboard.geraet.now", "jetzt")}</span>
            </div>
          </div>
        {/if}
        {#if eSummen.length > 0}
          <div
            class="grid gap-2 mt-2 pt-2 border-t border-border"
            style="grid-template-columns: repeat({eSummen.length}, minmax(0, 1fr))"
          >
            {#each eSummen as s (s)}
              {@const kwh = s === "heute" ? energy?.totals?.today_kwh : s === "monat" ? energy?.totals?.month_kwh : energy?.totals?.year_kwh}
              {@const eur = s === "heute" ? energy?.totals?.today_eur : s === "monat" ? energy?.totals?.month_eur : energy?.totals?.year_eur}
              <div>
                <div class="text-fg-subtle t-eyebrow">
                  {s === "heute"
                    ? t("dashboard.energie.today", "heute")
                    : s === "monat"
                      ? t("dashboard.energie.month", "Monat")
                      : t("dashboard.energie.year", "Jahr")}
                </div>
                <div class="tabular-nums t-body">{formatZahl(kwh)} kWh</div>
                {#if eKosten && eur != null}
                  <div class="text-fg-subtle tabular-nums t-meta">{formatZahl(eur, 2)} €</div>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </a>

  {:else if id === "wetter"}
      <a href="#/zuhause/wetter" class="surface border border-border rounded-xl px-4 py-3 card-hover shadow-sm transition block">
        <div class="flex items-center justify-between gap-2 mb-1">
          {@render kartenKopf(weather?.current ? weatherIconName(weather.current.icon, weather.current.is_day) : "cloud", t("dashboard.card.wetter", "Wetter"))}
          {#if weather?.current?.description}
            <span class="text-fg-subtle t-meta truncate max-w-[60%]">{weather.current.description}</span>
          {/if}
        </div>
        <div class="flex items-baseline gap-2">
          {#if weather?.current?.temp != null}
            <span class="t-kpi tabular-nums">{formatZahl(weather.current.temp)}</span>
            <span class="text-fg-muted t-headline">°C</span>
            {#if weather.current.feels_like != null && zeigtBaustein("wetter", "gefuehlt")}
              <span class="text-fg-subtle t-meta ml-auto">{t("dashboard.wetter.feels", "gefühlt")} {formatZahl(weather.current.feels_like, 0)}°</span>
            {/if}
          {:else}
            <span class="t-kpi tabular-nums">—</span>
            <span class="text-fg-muted t-meta">{t("common.loading", "lade…")}</span>
          {/if}
        </div>
        {#if ["feuchte", "wind", "wolken"].some((s) => zeigtBaustein("wetter", s))}
        <!-- Jede Angabe bricht als Ganzes um (schmale Karte): Zahl und
             Einheit bleiben zusammen. -->
        <div class="flex flex-wrap gap-x-3 gap-y-0.5 mt-2 pt-2 border-t border-border t-meta">
          {#if weather?.current?.humidity != null && zeigtBaustein("wetter", "feuchte")}
            <div class="flex items-center gap-1 whitespace-nowrap text-fg-muted">
              <Icon name="droplet" size={12}/>
              <span class="tabular-nums">{Math.round(weather.current.humidity)}{"\u00a0%"}</span>
              <span class="text-fg-subtle">{t("dashboard.wetter.humidity", "Feuchte")}</span>
            </div>
          {/if}
          {#if weather?.current?.wind_speed != null && zeigtBaustein("wetter", "wind")}
            <div class="flex items-center gap-1 whitespace-nowrap text-fg-muted">
              <Icon name="wind" size={12}/>
              <span class="tabular-nums">{formatZahl(weather.current.wind_speed, 0)}</span>
              <span class="text-fg-subtle">km/h</span>
            </div>
          {/if}
          {#if weather?.current?.cloud_cover != null && zeigtBaustein("wetter", "wolken")}
            <div class="flex items-center gap-1 whitespace-nowrap text-fg-muted">
              <Icon name="cloud" size={12}/>
              <span class="tabular-nums">{Math.round(weather.current.cloud_cover)}{"\u00a0%"}</span>
              <span class="text-fg-subtle">{t("dashboard.wetter.clouds", "Wolken")}</span>
            </div>
          {/if}
        </div>
        {/if}
        {#if forecastSlots.length > 0 && zeigtBaustein("wetter", "vorschau")}
          <div
            class="grid gap-1 mt-2 pt-2 border-t border-border"
            style="grid-template-columns: repeat({forecastSlots.length}, minmax(0, 1fr))"
          >
            {#each forecastSlots as f (f.time)}
              <div class="flex flex-col items-center gap-0.5">
                <span class="text-fg-subtle t-meta tabular-nums">{f.time.slice(11, 13)}</span>
                <Icon name={weatherIconFromCode(f.code, f.isDay)} size={14}/>
                <span class="t-meta tabular-nums">{Math.round(f.temp)}°</span>
                {#if f.precip >= 30}
                  <!-- 9px absichtlich kleiner als t-meta: 6-Spalten-Forecast,
                       Precip ist subordinate zur Temp daneben. -->
                  <span class="text-fg-subtle text-[9px] tabular-nums">{Math.round(f.precip)}{"\u00a0%"}</span>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </a>

      <!-- Klima · auffällige Räume -->
  {:else if id === "klima"}
      <a href="#/zuhause/sensoren" class="surface border border-border rounded-xl px-4 py-3 card-hover shadow-sm transition block">
        <div class="flex items-center justify-between gap-2 mb-1">
          {@render kartenKopf("thermo", t("dashboard.card.klima", "Klima"))}
          {#if klimaAuffaellig.length > 0}
            <StatusBadge tone={klimaAuffaellig.some((s) => s.status === "problem") ? "danger" : "warn"}>
              {klimaAuffaellig.length} {t("dashboard.klima.odd", "auffällig")}
            </StatusBadge>
          {:else if klimaSensoren.length > 0}
            <StatusBadge tone="ok">{klimaSensoren.length} {t("dashboard.kpi.ok", "ok")}</StatusBadge>
          {/if}
        </div>
        {#if klimaAuffaelligShown.length > 0}
          <!-- Auffaellig zuerst, max 2, in der Farbe ihres Befunds -->
          <ul class="space-y-0.5 mt-1">
            {#each klimaAuffaelligShown as s (s.sensor_id)}
              {@const farbe = s.status === "problem" ? "text-danger" : "text-warning"}
              <li class="flex items-baseline justify-between t-meta">
                <span class="flex items-center gap-1.5 truncate font-semibold {farbe}">
                  <Icon name={s.domain === 'co2' ? 'wind' : 'thermo'} size={11} class="shrink-0"/>
                  <span class="truncate">{s.sensor_id}</span>
                </span>
                <span class="font-medium tabular-nums shrink-0 ml-2 inline-flex items-baseline justify-end {farbe}">
                  <span class="text-right">{klimaValue(s)}</span>
                  <span class="text-fg-subtle t-meta inline-block w-9 text-left pl-0.5">{klimaUnit(s)}</span>
                </span>
              </li>
            {/each}
          </ul>
          <div class="border-t border-border my-1.5"></div>
        {/if}
        <!-- Zuletzt gemeldet -->
        {#if klimaLetzte.length === 0}
          <div class="text-fg-muted t-meta mt-1">{t("dashboard.klima.empty", "noch keine Daten")}</div>
        {:else}
          <ul class="space-y-0.5 mt-0.5">
            {#each klimaRecentShown as s (s.sensor_id)}
              <li class="flex items-baseline justify-between t-meta">
                <span class="text-fg-muted truncate">{s.sensor_id}</span>
                <!-- Fixed-width-Unit-Slot rechts (w-9 ~= "ppm" Breite),
                     damit die Zahlen unterschiedlich langer Werte
                     untereinander in einer Spalte landen. -->
                <span class="font-medium tabular-nums shrink-0 ml-2 inline-flex items-baseline justify-end">
                  <span class="text-right">{klimaValue(s)}</span>
                  <span class="text-fg-subtle t-meta inline-block w-9 text-left pl-0.5">{klimaUnit(s)}</span>
                </span>
              </li>
            {/each}
          </ul>
          {#if klimaHiddenCount > 0}
            <div class="text-fg-subtle t-meta mt-0.5">+{klimaHiddenCount} {t("dashboard.klima.more", "weitere")}</div>
          {/if}
        {/if}
        {#if klimaLetzte[0]?.ts}
          <div class="text-fg-subtle t-meta mt-1.5 pt-1.5 border-t border-border">{t("dashboard.geraet.last", "zuletzt")} {formatRelative(klimaLetzte[0].ts, now)}</div>
        {/if}
      </a>

      <!-- Netzfrequenz · europäisches Verbundnetz (internal/netzfrequenz).
           Die Karte führt in die Infrastruktur und trägt deren Farbe. -->
  {:else if id === "lueftung"}
      <!-- Lüftung & Heizung: drei Werte, darunter die Kurven der letzten
           24 h; grau hinterlegt, wann die Heizung lief (lib/dashboard/lueftung.ts). -->
      <a href="#/zuhause/sensoren" class="surface border border-border rounded-xl px-4 py-3 card-hover shadow-sm transition block">
        <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-1.5">
          {@render kartenKopf("wind", t("dashboard.card.lueftung", "Lüftung & Heizung"))}
          {#if lueftung.lage.length}
            <span class="text-fg-muted t-meta">{lueftung.lage.map((l) => l.text).join(" · ")}</span>
          {/if}
        </div>
        <div class="grid grid-cols-3 gap-2">
          {#each lueftungFuehler as f, i (f.schluessel)}
            {@const platz = [REIHE.lueftungAussen, REIHE.lueftungInnen, REIHE.heizung][i]}
            <div class="min-w-0">
              <!-- Strich in der Farbe der Kurve: bunt die Palette, sonst
                   die Stufe (wie Chart.seriesColor), statt der Legende. -->
              <div class="text-fg-muted t-meta truncate flex items-center gap-1.5">
                <span class="lf-strich" style="--lf-reihe: var(--reihe-{platz}); --lf-stufe: var(--stufe-{i + 1})"></span>{f.name}
              </div>
              <div class="flex items-baseline gap-1">
                <span class="t-kpi tabular-nums">{f.wert === null ? "—" : f.wert.toFixed(1).replace(".", ",")}</span>
                <span class="text-fg-muted t-meta">°C</span>
              </div>
            </div>
          {/each}
        </div>
        {#if lueftung.daten[0].length > 1}
          <div class="mt-2">
            <Chart
              data={lueftung.daten}
              labels={lueftungFuehler.map((f) => f.name)}
              plaetze={[REIHE.lueftungAussen, REIHE.lueftungInnen, REIHE.heizung]}
              unit="°C"
              height={150}
              decimals={1}
              zonen={lueftung.zonen}
              legende={false}
            />
          </div>
          <div class="text-fg-subtle t-meta mt-0.5">{t("dashboard.lueftung.zonen", "24 h · grau hinterlegt: Heizung lief")}</div>
        {/if}
      </a>

  {:else if id === "netzfrequenz"}
      {@const nf = getNetzfrequenz()}
      {@const ton = nf?.status === "problem" ? "text-danger" : nf?.status === "warning" ? "text-warning" : ""}
      <a href="#/infra/netzfrequenz" data-bereich="infra" class="surface border border-border rounded-xl px-4 py-3.5 card-hover shadow-sm transition block">
        <div class="flex items-center justify-between gap-2 mb-1">
          {@render kartenKopf("zap", t("dashboard.netzfrequenz.title", "Netzfrequenz"))}
          {#if nf?.status === "stale"}
            <StatusBadge tone="leise">{t("dashboard.netzfrequenz.stale", "keine Daten")}</StatusBadge>
          {:else if nf?.status === "problem"}
            <StatusBadge tone="danger">{t("dashboard.netzfrequenz.problem", "kritisch")}</StatusBadge>
          {:else if nf?.status === "warning"}
            <StatusBadge tone="warn">{t("dashboard.netzfrequenz.warning", "auffällig")}</StatusBadge>
          {:else if nf?.hz != null}
            <StatusBadge tone="ok">{t("dashboard.live", "live")}</StatusBadge>
          {/if}
        </div>
        <div class="flex items-baseline gap-2">
          <span class="t-kpi tabular-nums {ton}">{hzText(nf?.hz)}</span>
          <span class="text-fg-muted t-headline">Hz</span>
          <span class="t-meta tabular-nums ml-2 {ton || 'text-fg-subtle'}">{mhzText(nf?.deviation_mhz)}</span>
        </div>
        {#if (nf?.spark.length ?? 0) > 1 && zeigtBaustein("netzfrequenz", "kurve")}
          <!-- Die Quelle liefert Minutenmittel der letzten Stunde; ein
               kuerzeres Fenster ist der hintere Ausschnitt davon. -->
          {@const nfMin = Math.round((kartenConfig("netzfrequenz").stunden ?? 1) * 60)}
          {@const nfWerte = (nf?.spark ?? []).slice(-nfMin)}
          <div class="mt-2">
            <SparkLine values={nfWerte} fluid height={28} gridLines={3} platz={REIHE.frequenz} />
            <div class="flex justify-between text-fg-subtle text-[9px] mt-0.5 tabular-nums">
              <span>−{nfMin} min</span>
              <span>−{Math.round(nfMin / 2)} min</span>
              <span>{t("dashboard.geraet.now", "jetzt")}</span>
            </div>
          </div>
        {/if}
        {#if nf?.day && zeigtBaustein("netzfrequenz", "tagesbereich")}
          <!-- Die Einordnung zum Momentanwert: wie weit ist es heute
               ausgeschlagen, und wann war es zuletzt wirklich auffällig. -->
          <div class="text-fg-subtle t-meta mt-2 pt-2 border-t border-border tabular-nums flex flex-wrap gap-x-3">
            <span>24 h: {hzText(nf.day.min_hz)} – {hzText(nf.day.max_hz)} Hz</span>
            {#if nf.last_excursion}<span>{t("dashboard.netzfrequenz.last", "zuletzt auffällig")} {formatRelative(nf.last_excursion, now)}</span>{/if}
          </div>
        {/if}
      </a>
  {/if}
{/snippet}

{#snippet zuhauseCard(id: string)}
  {#if id === "szene"}
      <!-- Keine Link-Karte mehr um die Knöpfe herum (Knöpfe in einem Link
           sind ungültig und stahlen den Klick per stopPropagation): der
           Titel ist der Link und spannt sich über die Karte, die Knöpfe
           liegen darüber. -->
      <article class="karte surface border border-border rounded-xl px-4 py-3.5 card-hover shadow-sm transition">
        <div class="flex items-center justify-between gap-2 mb-1">
          <a href="#/zuhause/ambient" class="flaechen-link min-w-0">
            {@render kartenKopf("sun", ambientHeader)}
          </a>
          <!-- „Alles aus" ist eine Aktion der Karte: Symbol ohne Rahmen
               in der Kopfzeile, wie die Werkzeuge einer Sektion. -->
          <span class="relative z-10 -my-2 -mr-2">
            <SymbolKnopf
              icon="poweroff"
              label={t("dashboard.scene.all_off.tooltip", "Alle Lichter ausschalten")}
              disabled={allOffPending}
              onclick={() => void allOff()}
            />
          </span>
        </div>
        <div class="flex items-baseline gap-2 mb-2">
          <span class="t-headline">{ambientSzene}</span>
          <span class="text-fg-muted t-meta">{ambientSummary}</span>
        </div>
        {#if scenesForButtons.length > 0 || quickLights.length > 0}
          <!-- Szenen sind eine Wahl (eine ist aktiv), die beiden Lichter
               Umschalter -- beides als .wahl, gewählt/an mit kräftigem
               Rand. -->
          <div class="relative z-10 flex flex-wrap items-center gap-x-3 gap-y-2">
            {#if scenesForButtons.length > 0}
              <div class="wahl" role="group" aria-label={t("dashboard.card.szene", "Szenen")}>
                {#each scenesForButtons as s (s.name)}
                  <button
                    type="button"
                    aria-pressed={s.active === true}
                    disabled={activatingScene === s.name}
                    onclick={() => void activateScene(s.name)}
                  >
                    {s.name}
                  </button>
                {/each}
              </div>
            {/if}
            {#if quickLights.length > 0}
              <div class="wahl" role="group" aria-label={t("dashboard.card.lichter", "Lichter")}>
                {#each quickLights as l (l.name)}
                  <button
                    type="button"
                    aria-pressed={l.on}
                    disabled={togglingLight === l.name}
                    title={l.name}
                    onclick={() => void toggleQuickLight(l.name, l.on)}
                  >
                    <Icon name="bulb" size={12} />
                    {l.label}
                  </button>
                {/each}
              </div>
            {/if}
          </div>
        {/if}
      </article>

      <!-- Lichter -->
  {:else if id === "lichter"}
      <a href="#/zuhause/aktoren" class="surface border border-border rounded-xl px-4 py-3 card-hover shadow-sm transition block">
        <div class="flex items-center justify-between gap-2 mb-1">
          {@render kartenKopf("bulb", t("dashboard.card.licht", "Licht"))}
          {#if lichterLastTs}
            <span class="text-fg-subtle t-meta tabular-nums">{formatRelative(lichterLastTs, now)}</span>
          {/if}
        </div>
        <div class="flex items-baseline gap-1.5">
          <span class="t-kpi tabular-nums">{lichterAn}</span>
          <span class="text-fg-muted t-meta">/ {lichterGesamt} {t("dashboard.licht.on", "an")}</span>
        </div>
        {#if lichterAvgPct !== null}
          <div class="text-fg-muted t-meta">∅ {lichterAvgPct} % {t("dashboard.licht.brightness", "Helligkeit")}</div>
        {/if}
      </a>

      <!-- Tueren · offen/zu -->
  {:else if id === "tueren"}
      <a href="#/zuhause/sensoren" class="@container surface border border-border rounded-xl px-4 py-3 card-hover shadow-sm transition block">
        <div class="flex items-center justify-between gap-2 mb-1">
          {@render kartenKopf("door", t("dashboard.card.tueren_kurz", "Türen"))}
          {#if tueren.length > 0}
            {#if tuerenOffenCount === 0}
              <StatusBadge tone="ok">{t("dashboard.tueren.all_closed", "alle zu")}</StatusBadge>
            {:else}
              <StatusBadge tone={tuerZuLang ? "danger" : tuerenAlarmingOffen > 0 ? "warn" : "leise"}>{tuerenOffenCount} {t("dashboard.tueren.open", "offen")}</StatusBadge>
            {/if}
          {/if}
        </div>
        <!-- Name, seit wann, Zustand. Zu = ok, offen = Auskunft (neutral),
             eine offene Außentür = gelb (tuerTon). Bis 03.10. war jede
             offene Tür gelb getönt -- auch die Badtür, die offen stehen
             darf. „Seit wann" steht nur bei offenen Türen: dort ist es
             die Frage. Bei geschlossenen steht es im Tooltip, und in einer
             sehr schmalen Karte weicht es dem Namen (.tueren unten). -->
        <ul class="tueren t-meta mt-1">
          {#each tueren.slice(0, 4) as s (s.sensor_id)}
            {@const tuer = tuerVon(s)}
            <!-- „seit" ist die Flanke (since), nicht die letzte Funkmeldung
                 (ts): z2m meldet den Zustand immer wieder, daraus las sich
                 „offen vor 9 min", obwohl die Tür seit einer Stunde offen war. -->
            {@const seit = s.since ? formatRelative(s.since, now) : ""}
            <li title={seit ? `${s.sensor_id} · ${tuer.offen ? t("dashboard.tueren.open", "offen") : t("dashboard.tueren.closed", "zu")} ${seit}` : s.sensor_id}>
              <span class="truncate">{s.sensor_id}</span>
              <span class="seit text-fg-subtle text-right tabular-nums">{tuer.offen ? seit : ""}</span>
              <span class="justify-self-end"><StatusBadge tone={tuerTon(tuer)}>{tuer.offen ? t("dashboard.tueren.open", "offen") : t("dashboard.tueren.closed", "zu")}</StatusBadge></span>
            </li>
          {/each}
        </ul>
        {#if tueren.length > 4}
          <div class="text-fg-subtle t-meta mt-1.5 pt-1.5 border-t border-border">{tueren.length} {t("dashboard.tueren.total", "Türen gesamt")}</div>
        {/if}
      </a>
  {/if}
{/snippet}

<section class="grid gap-4 sm:gap-6">
  <!-- ╔══ SYSTEM — Ausnahme-Prinzip ═════════════════════════════╗
       Der Kopf sagt die Lage, die Kennzahlen stehen ruhig darunter. Ein
       Balken mit der dringendsten Meldung erscheint NUR, wenn es eine
       gibt. Jede Kennzahl trägt die Farbe des Bereichs, in den sie führt. -->
  <div data-bereich="system" class="@container" class:hidden={!dashSections.includes("system") && systemLead.sev !== "danger"}>
    <ZonenKopf
      icon="wrench"
      label={t("dashboard.section.system", "System")}
      href="#/system"
      lage={systemLage}
      lageHref={systemLead.href}
      lageTitle={systemLead.title}
    >
      <!-- Server-Uhr: friert ein und wird rot, wenn der Server nicht
           mehr atmet (Heartbeat). -->
      <span class="t-meta tabular-nums {hbStale ? 'text-danger' : 'text-fg-subtle'}">
        {#if forgeUptimeS != null}<span title={t("dashboard.system.uptime_title", "Homelab-Laufzeit (forge) seit Boot")}>{t("dashboard.system.running", "läuft seit")} {formatUptime(forgeUptimeS)}</span>{" · "}{/if}{clockText}
      </span>
    </ZonenKopf>

    {#if systemLead.sev !== "ok"}
      <!-- Die dringendste Meldung. Der Titel IST der Link (offene
           Meldung), sein ::after deckt die ganze Fläche ab; die Maßnahmen
           liegen per z-index darüber. -->
      <article
        class="karte surface mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border px-4 py-3 shadow-sm
          {systemLead.sev === 'danger' ? 'border-danger/50' : 'border-warning/50'}"
      >
        <a href={systemLead.href} title={systemLead.title} class="flaechen-link flex min-w-0 flex-1 items-center gap-3">
          <span class="h-2.5 w-2.5 shrink-0 rounded-full {systemLead.sev === 'danger' ? 'bg-danger' : 'bg-warning'}" aria-hidden="true"></span>
          <span class="min-w-0 leading-tight">
            <span class="t-headline line-clamp-2 block {systemLead.sev === 'danger' ? 'text-danger' : 'text-warning'}">{systemLead.label}</span>
            <span class="text-fg-subtle t-meta block">{systemLead.sub}</span>
          </span>
        </a>
        {#if kopfMassnahmen.length > 0}
          <!-- Massnahmen am kritischen Kopf: der kurze Weg zu einer Loesung
               mit bewusst gemachten Konsequenzen. -->
          <span class="relative z-10">
            <RemediationMenu
              remediations={kopfMassnahmen}
              label={t("dashboard.critical.remediate", "Maßnahmen")}
              triggerVariant="critical" />
          </span>
        {/if}
      </article>
    {/if}

    <!-- Fünf Kennzahlen, Infrastruktur doppelt breit: so sind die Zeilen
         in jeder Breite voll (2 Spalten: 2 / 1+1 / 1+1, drei: 2+1 / 1+1+1,
         sechs: alles in einer Zeile) -- keine einzelne Restkarte. Die
         Breite ist die der Zone, nicht die des Fensters. -->
    <div class="grid grid-cols-2 gap-3 @md:grid-cols-3 @3xl:grid-cols-6">
      <a href="#/infra/topologie" data-bereich="infra" class="kennzahl surface card-hover border-border col-span-2 rounded-xl border px-4 py-3 shadow-sm"
         title={t("dashboard.sys.infra_title", "Rechner · Agents · Erreichbarkeit — Details in der Topologie")}>
        {@render kennzahlKopf("server", t("dashboard.sys.infra", "Infrastruktur"), hosts != null && !infraAuffaellig)}
        <span class="t-kpi tabular-nums {(hosts?.hosts_offline ?? 0) > 0 ? 'text-warning' : ''}">
          {hosts ? hosts.hosts_online : "—"}<span class="text-fg-subtle t-body">/{hosts ? hosts.hosts_total : "—"}</span>
          <span class="text-fg-muted t-meta font-normal">{t("dashboard.sys.hosts", "Rechner")}</span>
        </span>
        <span class="text-fg-subtle t-meta tabular-nums">
          <span class={agentSummary.stale + agentSummary.neverSeen > 0 ? "text-warning font-medium" : ""}>{t("dashboard.sys.agents", "Agents")} {agentSummary.total > 0 ? `${agentSummary.ok}/${agentSummary.total}` : "—"}</span>{" · "}<span class={uptimeCard.down.length > 0 ? "text-warning font-medium" : ""}>{t("dashboard.sys.targets", "Ziele")} {uptimeCard.total > 0 ? `${uptimeCard.up}/${uptimeCard.total}` : "—"}</span>
        </span>
      </a>

      <a href="#/infra/network" data-bereich="infra" class="kennzahl surface card-hover border-border rounded-xl border px-4 py-3 shadow-sm"
         title={t("dashboard.sys.wan_title", "WAN-Bandbreite (OpenWrt-Agent) — Details auf der Netzwerk-Seite")}>
        {@render kennzahlKopf("globe", t("dashboard.wan.title", "Internet"), internetAllesOk(wan))}
        <span class="t-kpi tabular-nums {wanTon}">
          <span class="text-fg-subtle t-body">↓</span> {wan ? formatZahl(wan.rx_mbps ?? 0) : "—"}
        </span>
        <span class="text-fg-subtle t-meta tabular-nums">↑ {wan ? formatZahl(wan.tx_mbps ?? 0) : "—"} Mbit/s{#if wanWeitere.length > 0 && wanHier}{" · "}{routerName(wanHier)}{/if}</span>
        <!-- Weitere Router: je einer eine Zeile, Bandbreite oder Zustand in
             Worten. Farbe nur für einen Befund. -->
        {#each wanWeitere as w (w.name)}
          <span class="t-meta tabular-nums {w.ton === 'danger' ? 'text-danger font-medium' : w.ton === 'warn' ? 'text-warning font-medium' : 'text-fg-subtle'}">{w.name} {w.text}</span>
        {/each}
      </a>

      <!-- Ein Task auf rot hebt die Karte klar ab: voller roter Rahmen,
           kein blasser Tint (Tim). -->
      <a href="#/system/tasks" class="kennzahl surface card-hover border-border rounded-xl border px-4 py-3 shadow-sm" class:rot={tasksRot > 0}
         title={t("dashboard.sys.tasks_title", "Läufe in 24 h — rot, wenn ein Task GERADE auf rot steht")}>
        {@render kennzahlKopf("checks", t("dashboard.sys.tasks", "Tasks"), tasks != null && tasksRot === 0)}
        {#if tasksRot > 0}
          <span class="t-kpi tabular-nums text-danger">{tasksRot}</span>
          <span class="text-danger t-meta font-medium">{tasksRot === 1 ? t("dashboard.sys.task_red", "steht auf rot") : t("dashboard.sys.tasks_red", "stehen auf rot")}</span>
        {:else}
          <span class="t-kpi tabular-nums">{tasks ? tasks.ok_last_24h.toLocaleString("de-DE") : "—"}</span>
          <span class="text-fg-subtle t-meta">{t("dashboard.sys.tasks_sub", "ok in 24 h")}</span>
        {/if}
      </a>

      <a href="#/infra/storage" data-bereich="infra" class="kennzahl surface card-hover border-border rounded-xl border px-4 py-3 shadow-sm">
        {@render kennzahlKopf("hdd", t("dashboard.sys.storage", "Storage"), storage?.status === "ok")}
        <span class="t-kpi tabular-nums {storageTon === 'danger' ? 'text-danger' : storageTon === 'warning' ? 'text-warning' : ''}">
          {storage ? Math.round(storage.pct_used) : "—"}<span class="text-fg-subtle t-body">{"\u00a0%"}</span>
        </span>
        <!-- Füllstand: neutral, Farbe erst ab der Warn-Schwelle (Farbe
             heißt Zustand) -- bis 03.10. war der Balken immer Akzent. -->
        <span class="flex items-center gap-2">
          <span class="bg-fg/10 block h-1 flex-1 overflow-hidden rounded-full" aria-hidden="true">
            {#if storage}
              <span
                class="block h-full rounded-full {storageTon === 'danger' ? 'bg-danger' : storageTon === 'warning' ? 'bg-warning' : 'bg-fg/45'}"
                style="width: {Math.min(100, Math.max(0, storage.pct_used))}%"
              ></span>
            {/if}
          </span>
          <span class="text-fg-subtle t-meta">{t("dashboard.sys.storage_sub", "belegt")}</span>
        </span>
      </a>

      <!-- Aktive Bans sind kein Befund: die Sperre greift. Die Zahl steht
           darum ohne Farbe (Tim 04.10.2026: „Bans können leise werden").
           Dass etwas gebannt WURDE, sagt je Ban ein Hinweis in den
           Meldungen -- sehen, prüfen, als gelesen abhaken. -->
      <a href="#/sicherheit" data-bereich="sec" class="kennzahl surface card-hover border-border rounded-xl border px-4 py-3 shadow-sm">
        {@render kennzahlKopf("shield", t("dashboard.sys.bans", "Bans"), security != null && (security.active_bans ?? 0) === 0)}
        <span class="t-kpi tabular-nums">{security?.active_bans ?? 0}</span>
        <span class="text-fg-subtle t-meta">{t("dashboard.sys.bans_sub", "aktiv")}</span>
      </a>
    </div>
  </div>

  <!-- ╔══ ENERGIE & KLIMA ════════════════════════════════════════╗ -->
  <div data-bereich="home" class:hidden={!dashSections.includes("energie")}>
    <ZonenKopf icon="zap" label={t("dashboard.section.energy_climate", "Energie & Klima")} href="#/zuhause" lage={energieLage}>
      {@render arrangeBar("energie")}
    </ZonenKopf>
    <CardGrid
      bind:arranging={zoneArranging.energie}
      group="dashboard_energie"
      ids={[...ZONE_DEFAULTS.energie, ...geraeteIds]}
      card={energieCard}
      defaultWide={["energie", "lueftung"]}
      labelOf={kartenTitel}
      onConfigure={(id) => (configKarte = id.startsWith("geraet:") || FESTE_VORGABE[id] ? id : null)}
    />
  </div>

  <!-- ╔══ ZUHAUSE ════════════════════════════════════════════════╗ -->
  <div data-bereich="home" class:hidden={!dashSections.includes("zuhause")}>
    <ZonenKopf icon="bulb" label={t("dashboard.section.home", "Zuhause")} href="#/zuhause" lage={zuhauseLageTeile}>
      {@render arrangeBar("zuhause")}
    </ZonenKopf>
    <CardGrid
      bind:arranging={zoneArranging.zuhause}
      group="dashboard_zuhause"
      ids={ZONE_DEFAULTS.zuhause}
      card={zuhauseCard}
      defaultWide={["szene"]}
      labelOf={kartenTitel}
    />
  </div>

  <!-- ╔══ LIVE ═══════════════════════════════════════════════════╗ -->
  <div data-bereich="system" class:hidden={!dashSections.includes("live")}>
    <ZonenKopf
      icon="history"
      label={t("dashboard.section.live", "Live")}
      href="#/system/events"
      lage={eventsRendered.length > 0 ? [{ text: `${t("dashboard.live.last", "letzte Aktion")} ${eventsRendered[0].rel}` }] : []}
    >
      <a href="#/system/events" class="text-fg-subtle hover:text-marke inline-flex items-center gap-0.5 t-meta transition-colors">
        {t("dashboard.live.all", "alle Ereignisse")}
        <Icon name="arrow-right" size={13} />
      </a>
    </ZonenKopf>
    <!-- Zwei Spalten erst, wenn DIESE Fläche breit genug ist (Container),
         nicht das Fenster -- sonst schneidet die geteilte Ansicht die
         Meldungstexte ab. -->
    <article class="@container surface border border-border rounded-xl px-3 py-2 shadow-sm">
      {#if feedRows.length === 0}
        <p class="text-fg-muted t-meta">{t("dashboard.live.empty", "Noch nichts aufgezeichnet.")}</p>
      {:else}
        <ul class="grid grid-cols-1 @3xl:grid-cols-2 gap-x-6 gap-y-0 t-meta">
          {#each feedRows as row (row.kind === "cluster" ? row.key : (row.ev.id ?? row.ev.ts + row.ev.summary))}
            <li class="border-b border-border/60 last:border-b-0">
              {#if row.kind === "cluster"}
                {@const open = openClusters.has(row.key)}
                <button type="button" onclick={() => toggleCluster(row.key)}
                  class="w-full min-h-8 flex items-center gap-2 text-left hover:bg-fg/5 rounded transition-colors"
                  aria-expanded={open}
                  title={t("dashboard.live.cluster_title", "Task-Läufe im selben Zeitfenster — aufklappen für Details")}>
                  <span class="text-fg-subtle tabular-nums w-[3.9rem] shrink-0">{row.rel}</span>
                  <Schild>tasks</Schild>
                  <span class="flex-1 min-w-0 truncate">
                    <Lage teile={[{ text: `${row.items.length} ${t("dashboard.live.cluster", "Tasks ok")}`, ton: "ok" }]} />
                  </span>
                  <span class="shrink-0 text-fg-subtle transition-transform {open ? 'rotate-90' : ''}"><Icon name="chevron-right" size={13}/></span>
                </button>
                {#if open}
                  <!-- Die einzelnen Läufe als Text, nicht als gerahmte
                       Pillen: ein Klick führt zu den Tasks. -->
                  <div class="flex flex-wrap gap-x-3 gap-y-0.5 pb-2 pl-[4.4rem]">
                    {#each row.items as it (it.id ?? it.ts + it.summary)}
                      <button type="button" onclick={() => openEvent(it)}
                        class="text-fg-muted hover:text-fg tabular-nums transition-colors">
                        {it.taskName} <span class="text-fg-subtle">{it.taskDur}</span>
                      </button>
                    {/each}
                  </div>
                {/if}
              {:else}
                {@const ev = row.ev}
                {@const target = eventClickTarget(ev)}
                {#if target}
                  <button type="button" onclick={() => openEvent(ev)} class="w-full min-h-8 flex items-center gap-2 text-left hover:bg-fg/5 rounded transition-colors">
                    <span class="text-fg-subtle tabular-nums w-[3.9rem] shrink-0">{ev.rel}</span>
                    <Schild>{ev.domain}</Schild>
                    <span class="flex-1 min-w-0 truncate {zeilenTon(ev.severity)}">{ev.summary}</span>
                  </button>
                {:else}
                  <div class="min-h-8 flex items-center gap-2">
                    <span class="text-fg-subtle tabular-nums w-[3.9rem] shrink-0">{ev.rel}</span>
                    <Schild>{ev.domain}</Schild>
                    <span class="flex-1 min-w-0 truncate {zeilenTon(ev.severity)}">{ev.summary}</span>
                  </div>
                {/if}
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </article>
  </div>
</section>


<style>
  /* Stretched Link: der Titel-Link deckt ueber ::after die ganze Karte
     ab, ohne dass die Karte selbst interaktiv sein muss (Knoepfe darin
     bleiben eigene Ziele und liegen per z-index darueber). */
  .karte {
    position: relative;
  }
  .flaechen-link::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }
  /* Der Fokusring gehoert an die Karte, nicht an den unsichtbar
     aufgespannten Link. */
  .karte:has(.flaechen-link:focus-visible) {
    outline: 2px solid var(--marke);
    outline-offset: 2px;
  }
  .flaechen-link:focus-visible {
    outline: none;
  }

  /* Kennzahl: Etikett, Zahl, eine Zeile Einordnung. Die Werte aendern
     sich laufend (Bandbreite, Task-Zaehler) -- die Breite kommt aus dem
     Raster, nicht aus dem Inhalt, damit nichts springt. */
  .kennzahl {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
    text-decoration: none;
  }
  .kennzahl.rot {
    border-color: var(--danger);
    box-shadow: inset 0 0 0 1px var(--danger);
  }

  /* Tueren: Spalten ueber alle Zeilen -- Name, seit wann, Zustand. Die
     Karte ist der Container: unter 11rem Inhalt faellt "seit wann" weg,
     damit der Name lesbar bleibt. */
  .tueren {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    column-gap: 0.6rem;
    row-gap: 0.3rem;
  }
  .tueren > li {
    display: contents;
  }
  .tueren .seit {
    display: none;
  }
  @container (min-width: 11rem) {
    .tueren {
      grid-template-columns: minmax(0, 1fr) auto auto;
    }
    .tueren .seit {
      display: block;
    }
  }
  /* Karte „Lüftung & Heizung": Strich in der Farbe der Kurve. */
  .lf-strich {
    width: 0.8rem;
    height: 3px;
    border-radius: 2px;
    flex: none;
    background: var(--lf-stufe);
  }
  :global(html[data-farbe-diagramme="bunt"]) .lf-strich {
    background: var(--lf-reihe);
  }
</style>

{#if configKarte}
  {@const d = geraetVon(configKarte)}
  {@const cfg = kartenConfig(configKarte)}
  {@const speichern = (c: CardConfig) => configKarte && setCardConfig("dashboard_energie", configKarte, c)}
  {@const zuruecksetzen = () => { if (configKarte) resetCardConfig("dashboard_energie", configKarte); }}
  {#if d}
    <CardConfigDialog
      titel={d.label || d.name}
      felder={sensoren(d).map((e) => ({ slug: e.slug, name: e.name, unit: e.unit }))}
      config={cfg}
      onChange={speichern}
      onReset={zuruecksetzen}
      onClose={() => (configKarte = null)}
    />
  {:else if FESTE_VORGABE[configKarte]}
    <!-- Feste Karte: keine Messwert-/Darstellungs-Wahl, dafuer ihre
         eigenen Bausteine (bzw. bei Klima ihre Sensoren). -->
    {@const istKlima = configKarte === "klima"}
    <CardConfigDialog
      titel={kartenTitel(configKarte)}
      felder={istKlima
        ? klimaAlle.map((s) => ({ slug: s.sensor_id, name: s.sensor_id, unit: klimaUnit(s) }))
        : FESTE_BAUSTEINE[configKarte].map((b) => ({ ...b, unit: "" }))}
      config={cfg}
      zeigeDarstellung={false}
      zeigeLeit={false}
      felderTitel={istKlima
        ? t("dashboard.cfg.klima.sensors", "Sensoren auf der Karte")
        : t("dashboard.cfg.blocks", "Was die Karte zeigt")}
      zahlen={FESTE_ZAHLEN[configKarte] ?? []}
      onChange={speichern}
      onReset={zuruecksetzen}
      onClose={() => (configKarte = null)}
    />
  {/if}
{/if}
